/**
 * Find Time + slots-preview + availability UI concern for the Details
 * step (self-schedule).
 *
 * Extracted verbatim from `DetailsStep` (pure file separation — no
 * behaviour change). Owns: the `findTimeValue` dirty-tracked preset,
 * the apply/clear handlers, the slots-preview modal open state +
 * grouped slots, the multi-week `availableDays` set, and the
 * no-availability remount key.
 *
 * `scheduleContextRef` deliberately stays in `DetailsStep` — it is a
 * save-time payload-input snapshot, not a Find Time concern.
 */
import { useCallback, useMemo, useRef, useState } from 'react';
import moment from 'moment-timezone';
import type {
  FindTimeApplyArgs,
  FindTimeValue,
  SelfScheduleHandle,
} from '../../../calendar/selfSchedule';
import type { ScheduleSharedState } from '../../../state/useScheduleSharedState';

interface UseFindTimeControllerArgs {
  selfScheduleRef: React.RefObject<SelfScheduleHandle | null>;
  sharedState: ScheduleSharedState;
}

export function useFindTimeController({
  selfScheduleRef,
  sharedState,
}: UseFindTimeControllerArgs) {
  // `findTimeValue` is null when the user hasn't picked a preset
  // or has manually edited after applying one (dirty tracking).
  // The walker anchors on today and doesn't take an availability
  // oracle — it picks target dates unconditionally and the
  // pipeline handles what's actually renderable.
  const [findTimeValue, setFindTimeValue] = useState<FindTimeValue>(null);

  // Suppresses the dirty-reset the next time `onSlotsChange`
  // fires — we set it right before dispatching `applyFindTime`
  // so the flood of state updates that follows doesn't null out
  // the user's just-picked preset.
  const ignoreNextSlotEmitRef = useRef(false);

  // Snapshot of `availableDays` for the apply handler — keeps the
  // callback identity stable while still reading the latest pipeline
  // emit. The walker intentionally omits availableDays from picking
  // (today-anchored, no drift) but we still use it post-pick to
  // detect "nothing on this week" and trigger the banner.
  const availableDaysRef = useRef<ReadonlySet<string> | undefined>(undefined);

  // Fresh key per Find-Time-no-availability event remounts the
  // popover so its internal "dismissed" state resets — without it
  // a user who dismissed once wouldn't see the next prompt.
  const [noAvailabilityKey, setNoAvailabilityKey] = useState<string | null>(
    null
  );

  const handleFindTimeApply = useCallback(
    ({ value, targetDateKeys, firstDateKey }: FindTimeApplyArgs) => {
      ignoreNextSlotEmitRef.current = true;
      selfScheduleRef.current?.applyFindTime({ targetDateKeys });
      setFindTimeValue(value);
      // Always navigate to the first target's week when Find
      // Time is applied. Covers both cases:
      //   - user clicks "Find time this week" from a different
      //     week → calendar scrolls back to today's week
      //   - user clicks "Next week" from current week →
      //     calendar scrolls forward to that week
      // The self-schedule calendar's `goToDate` imperatively
      // calls FullCalendar's `gotoDate`, which moves to the
      // week containing the given date.
      if (firstDateKey) {
        selfScheduleRef.current?.goToDate(firstDateKey);
      }
      // No-availability banner. Fire when the pipeline has reported
      // back at least once AND none of the resolved dates carry a
      // bookable slot — covers "Find time this week" against an empty
      // week (interviewers PTO, custom schedules, etc.). When the
      // emit hasn't arrived yet (initial load) we stay silent rather
      // than false-positive.
      const days = availableDaysRef.current;
      if (days) {
        const anyAvailable = targetDateKeys.some((k) => days.has(k));
        if (!anyAvailable)
          setNoAvailabilityKey(`${value ?? 'unknown'}-${Date.now()}`);
      }
    },
    [selfScheduleRef]
  );

  const handleFindTimeClear = useCallback(() => {
    ignoreNextSlotEmitRef.current = true;
    selfScheduleRef.current?.clearAll();
    setFindTimeValue(null);
  }, [selfScheduleRef]);

  // Preview modal — opened from the FindTimeField's "Preview"
  // link, sends the current slot set + interview metadata into
  // the candidate-app iframe via postMessage (see
  // `SlotsPreviewModal`).
  const [isPreviewOpen, setPreviewOpen] = useState(false);
  const handlePreviewOpen = useCallback(() => setPreviewOpen(true), []);
  const handlePreviewClose = useCallback(() => setPreviewOpen(false), []);

  // Group slots by date for the preview iframe. Key must be derived
  // in the wizard timezone — `split('T')[0]` would land late-night
  // local slots under the prior UTC day.
  const selfScheduleSlotsForPreview = useMemo(() => {
    const result: Record<
      string,
      Array<{ start_time: string; end_time: string }>
    > = {};
    const tz = sharedState.timezone;
    for (const slot of sharedState.selfScheduleSlots ?? []) {
      const dateKey = moment.tz(slot.start, tz).format('YYYY-MM-DD');
      if (!result[dateKey]) result[dateKey] = [];
      result[dateKey].push({ start_time: slot.start, end_time: slot.end });
    }
    for (const dateKey of Object.keys(result)) {
      result[dateKey].sort((a, b) => a.start_time.localeCompare(b.start_time));
    }
    return result;
  }, [sharedState.selfScheduleSlots, sharedState.timezone]);

  // Multi-week availability flowing from the calendar's pipeline
  // — `YYYY-MM-DD` keys for every day with at least one bookable
  // slot. Drives the Find Time walker so picks land on real
  // availability instead of the hardcoded Mon-Fri set. `undefined`
  // until the first emit; walker falls back to legacy weekday
  // behaviour while it's missing.
  const [availableDays, setAvailableDays] = useState<
    ReadonlySet<string> | undefined
  >(undefined);
  const handleAvailableDaysChange = useCallback((keys: string[]) => {
    // Bail when the new keys match current content. Without this,
    // every emit creates a fresh Set whose reference inequality
    // re-renders DetailsStep → CalendarPanel → SelfScheduleCalendar,
    // and the calendar's `useEffect` re-emits the same keys, looping
    // forever and breaking FC mid-drag (resetOptions wipes the
    // in-flight drag's `validMutation` so `eventDrop` never fires).
    const prev = availableDaysRef.current;
    if (prev && prev.size === keys.length && keys.every((k) => prev.has(k))) {
      return;
    }
    const next = new Set(keys);
    availableDaysRef.current = next;
    setAvailableDays(next);
  }, []);

  const handleFindTimeNoAvailability = useCallback((mode: FindTimeValue) => {
    setNoAvailabilityKey(`${mode ?? 'unknown'}-${Date.now()}`);
  }, []);

  // Auto-dismiss hook for the "no slots available" banner — callers
  // invoke this from event handlers (e.g. `onSlotsChange`) when slots
  // appear after the banner was raised. Prefer event-driven clear over
  // a useEffect on `sharedState.selfScheduleSlots`: avoids the
  // set-state-in-effect smell and runs in the same tick the slot emit
  // arrives, so the banner never lingers a frame past its purpose.
  const clearNoAvailability = useCallback(() => {
    setNoAvailabilityKey(null);
  }, []);

  return {
    findTimeValue,
    setFindTimeValue,
    ignoreNextSlotEmitRef,
    handleFindTimeApply,
    handleFindTimeClear,
    isPreviewOpen,
    handlePreviewOpen,
    handlePreviewClose,
    selfScheduleSlotsForPreview,
    availableDays,
    handleAvailableDaysChange,
    noAvailabilityKey,
    handleFindTimeNoAvailability,
    clearNoAvailability,
  };
}
